import type { OpusDecoder } from "opus-decoder";
import { RateModel } from "../utils/rate-model";
import {
  AudioDecoderError,
  SDKError,
  createUnknownError,
  withErrorData,
} from "../types/error.types";
import { isEndOfSentence } from "../utils/text-helpers";
import type {
  StreamData,
  TextFragment,
  AudioData,
  StreamDataHeader,
  StreamFormat,
} from "../types/model.types";
import { isTextStreamFormat, isAudioStreamFormat } from "../utils/type-helpers";
import { ThroughputTracker } from "../utils/throughput-tracker";

// Dynamic import to avoid SSR issues - WASM decoder requires browser environment
let OpusDecoderClass: typeof OpusDecoder | null = null;
async function getOpusDecoder(): Promise<typeof OpusDecoder> {
  if (!OpusDecoderClass) {
    const module = await import("opus-decoder");
    OpusDecoderClass = module.OpusDecoder;
  }
  return OpusDecoderClass;
}

/**
 * AudioTextDecoder class to handle the decoding of audio and text streams.
 */
export class AudioTextDecoder {
  private format: StreamFormat;
  private textFragmentCharacterLimit: number;
  private onStreamData: (streamData: StreamData) => void;
  private onError: (error: SDKError) => void;

  private textDecoder = new TextDecoder();
  private opusDecoder: OpusDecoder<48000> | null = null;
  private isDecoderInitialized: boolean = false;
  private rateModel: RateModel = new RateModel();
  private throughputTracker: ThroughputTracker = new ThroughputTracker("audio-text-decode");

  constructor(options: {
    format: StreamFormat;
    textFragmentCharacterLimit?: number;
    onStreamData: (streamData: StreamData) => void;
    onError: (error: SDKError) => void;
  }) {
    this.format = options.format;
    this.textFragmentCharacterLimit = options.textFragmentCharacterLimit || 50;
    this.onStreamData = options.onStreamData;
    this.onError = options.onError;
  }

  private async initializeDecoder(): Promise<void> {
    if (this.isDecoderInitialized) return;

    try {
      // Dynamically import to avoid SSR issues
      const DecoderClass = await getOpusDecoder();
      this.opusDecoder = new DecoderClass({
        sampleRate: 48000,
        channels: 2,
        forceStereo: true,
        preSkip: 0,
      }) as OpusDecoder<48000>;

      await this.opusDecoder.ready;
      this.isDecoderInitialized = true;
    } catch (error) {
      const sdkError = withErrorData(
        AudioDecoderError.OpusDecoderInitializationFailed,
        {
          details: error instanceof Error ? error.message : String(error),
        },
      );
      this.onError(sdkError);
      throw sdkError;
    }
  }

  async decode(data: ArrayBuffer, receiveTime?: number) {
    try {
      const { header, headerLength } = this.decodeHeader(data);

      const streamData: StreamData = {
        audioStreamData: null,
        textStreamData: null,
        serverIngestTimeMs:
          header.ingest_time_ms ||
          header.synthesizer_publish_time -
            header.source_duration_micro / 1000 ||
          null,
        sinceLastMicro: header.since_last_micro || null,
        minStartTime: null,
        isEndOfSentence: header.has_eos ?? true,
        receiveTime: receiveTime || Date.now(), // Store receive time for delay calculation
      };

      if (isAudioStreamFormat(this.format)) {
        const audioData = data.slice(4 + headerLength);
        if (audioData.byteLength > 0) {
          const audioStreamData = await this.processAudio(
            audioData,
            receiveTime,
            header.uuid,
          );
          if (audioStreamData) {
            streamData.audioStreamData = audioStreamData;
            header.output_duration_micro =
              (audioStreamData.frameLength / audioStreamData.sampleRate) *
              1000000;
          }
        }
      }

      this.rateModel.process(header);

      if (isTextStreamFormat(this.format)) {
        streamData.textStreamData = {
          type: "speech",
          fullTranslationText: null,
          translationStreamFragments: [],
          sourceStreamFragment: null,
          speakerLabel: header.speaker_label ?? null,
        };
        if (header.audio_event) {
          streamData.textStreamData = {
            type: "audioEvent",
            text: header.audio_event,
          };
        } else {
          if (header.source_texts) {
            const sourceFragment: TextFragment = {
              text: header.source_texts,
              duration: this.rateModel.playbackDuration * 0.9,
              isEndOfSentence: header.has_eos || false,
              speakerLabel: header.speaker_label ?? null,
            };
            streamData.textStreamData.sourceStreamFragment = sourceFragment;
          }

          streamData.textStreamData.translationStreamFragments =
            this.generateTextStream(
              header,
              this.rateModel.playbackDuration,
              this.textFragmentCharacterLimit,
            );
        }
      }

      this.onStreamData(streamData);
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "group" in error &&
        "code" in error
      ) {
        // Already an SDKError, pass it through
        this.onError(error as SDKError);
      } else {
        // Convert to unknown error with full stack trace
        const sdkError = createUnknownError(error, {
          context: "decode",
          method: "audio-text-decoder.decode",
          streamFormat: this.format,
          timestamp: receiveTime || Date.now(),
        });
        this.onError(sdkError);
      }
    }
  }

  async reset() {
    if (this.opusDecoder) {
      try {
        await this.opusDecoder.reset();
      } catch (error) {
        const sdkError = withErrorData(AudioDecoderError.DecoderResetFailed, {
          originalError: error instanceof Error ? error.message : String(error),
        });
        this.onError(sdkError);
      }
    }
    this.rateModel = new RateModel();
  }

  private decodeHeader(data: ArrayBuffer): {
    header: StreamDataHeader;
    headerLength: number;
  } {
    try {
      const view = new DataView(data);
      const headerLength = view.getUint32(0);
      const slice = data.slice(4, headerLength + 4);
      const header = JSON.parse(this.textDecoder.decode(slice));
      return { header, headerLength };
    } catch (error) {
      const sdkError = withErrorData(AudioDecoderError.HeaderDecodingFailed, {
        reason:
          error instanceof Error
            ? error.message
            : "Failed to parse header JSON",
      });
      throw sdkError;
    }
  }

  private generateTextStream(
    header: any,
    duration: number,
    characterLimit: number,
  ): TextFragment[] {
    const text = header.text || "";
    const words = text.split(" ");
    const fragments: TextFragment[] = [];
    let currentFragment = "";

    for (const word of words) {
      if ((currentFragment + " " + word).length <= characterLimit) {
        currentFragment += (currentFragment ? " " : "") + word;
      } else {
        if (currentFragment) {
          // Calculate duration based on character count for more accurate timing
          const fragmentDuration = this.calculateFragmentDuration(
            currentFragment,
            duration,
            text,
          );
          fragments.push({
            text: currentFragment,
            duration: fragmentDuration,
            isEndOfSentence: false,
            speakerLabel: header.speaker_label ?? null,
          });
        }
        currentFragment = word;
      }
    }

    if (currentFragment) {
      // Calculate duration based on character count for more accurate timing
      const fragmentDuration = this.calculateFragmentDuration(
        currentFragment,
        duration,
        text,
      );
      fragments.push({
        text: currentFragment,
        duration: fragmentDuration,
        isEndOfSentence: header.is_eos || isEndOfSentence(currentFragment),
        speakerLabel: header.speaker_label ?? null,
      });
    }

    return fragments;
  }

  private calculateFragmentDuration(
    fragment: string,
    totalDuration: number,
    fullText: string,
  ): number {
    // Calculate duration based on character count for more accurate timing
    const fragmentCharCount = fragment.length;
    const totalCharCount = fullText.length;

    if (totalCharCount === 0) return totalDuration;

    // Use character-based proportion with a minimum duration of 0.5 seconds
    const proportionalDuration =
      (fragmentCharCount / totalCharCount) * totalDuration;
    // Multiply by 0.9 to ensure text is displayed before paired audio.
    return proportionalDuration * 0.9;
  }

  private async processAudio(
    audioData: ArrayBuffer,
    receiveTime?: number,
    uuid?: string,
  ): Promise<AudioData | null> {
    try {
      if (!this.isDecoderInitialized) {
        await this.initializeDecoder();
      }

      if (!this.opusDecoder) {
        throw AudioDecoderError.DecoderNotInitialized;
      }

      let bytePos = 0;
      let opusFrames: Uint8Array[] = [];

      while (bytePos < audioData.byteLength) {
        const headerLen = 4;
        const header = audioData.slice(bytePos, bytePos + headerLen);

        if (header.byteLength < headerLen) {
          throw AudioDecoderError.InvalidOpusFrameHeader;
        }

        const headerView = new DataView(header);
        const nextFrameLen = headerView.getUint32(0);
        const nextFrameEnd = bytePos + headerLen + nextFrameLen;

        if (nextFrameEnd > audioData.byteLength) {
          throw AudioDecoderError.InvalidOpusFrameLength;
        }

        const opusFrame = audioData.slice(bytePos + headerLen, nextFrameEnd);
        opusFrames.push(new Uint8Array(opusFrame));

        bytePos = nextFrameEnd;
      }

      const decodedAudio = this.opusDecoder.decodeFrames(opusFrames);

      let samplesDecoded = decodedAudio.samplesDecoded;

      this.throughputTracker.receive(samplesDecoded);

      const streamData: AudioData = {
        timestamp: receiveTime || Date.now(),
        channelData: decodedAudio.channelData,
        sampleRate: decodedAudio.sampleRate,
        frameLength: samplesDecoded,
        playbackRate: this.rateModel.playbackRate,
        uuid: uuid || "",
      };

      opusFrames = [];

      return streamData;
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "group" in error &&
        "code" in error
      ) {
        // Already an SDKError, pass it through
        this.onError(error as SDKError);
      } else {
        // Convert to unknown error with full stack trace
        const sdkError = createUnknownError(error, {
          context: "processAudio",
          method: "audio-text-decoder.processAudio",
          streamFormat: this.format,
        });
        this.onError(sdkError);
      }
      return null;
    }
  }
}
